import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Shield, Users, KeyRound, LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badge = (type) =>
  type === "investor"
    ? "bg-[#d4af37]/15 text-[#d4af37] border-[#d4af37]/40"
    : "bg-white/5 text-[#a1a3a1] border-white/15";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [code, setCode] = useState(sessionStorage.getItem("admin_code") || "");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = useCallback((c) => ({ headers: { "X-Admin-Code": c } }), []);

  const fetchData = useCallback(async (c) => {
    setLoading(true);
    try {
      const [l, a] = await Promise.all([
        axios.get(`${API}/admin/leads`, headers(c)),
        axios.get(`${API}/admin/access-logs`, headers(c)),
      ]);
      setLeads(l.data);
      setLogs(a.data);
      setAuthed(true);
      sessionStorage.setItem("admin_code", c);
    } catch {
      setError("Codice non valido.");
      setAuthed(false);
      sessionStorage.removeItem("admin_code");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    const saved = sessionStorage.getItem("admin_code");
    if (saved) fetchData(saved);
  }, [fetchData]);

  const login = (e) => {
    e.preventDefault();
    setError("");
    fetchData(code);
  };

  const logout = () => {
    sessionStorage.removeItem("admin_code");
    setAuthed(false);
    setCode("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a0c0b] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="max-w-sm w-full">
          <div className="flex items-center gap-3 mb-8">
            <Shield size={22} className="text-[#d4af37]" />
            <h1 className="font-serif-display text-3xl text-[#f2f0e9]">Admin — Golf & Sea</h1>
          </div>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            <input required type="password" className="input-dark tracking-[0.2em]" placeholder="Codice admin"
              value={code} onChange={(e) => setCode(e.target.value)} data-testid="admin-code-input" />
            <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="admin-login-btn">
              {loading ? "Verifica…" : "Accedi"}
            </button>
            {error && <p className="text-sm text-red-400" data-testid="admin-error-msg">{error}</p>}
          </form>
          <a href="/" className="block mt-8 text-xs text-[#8c877d] hover:text-[#d4af37] transition-colors duration-300" data-testid="admin-back-link">← Torna al deck</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0c0b] text-[#f2f0e9]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-[#d4af37]" />
          <span className="font-serif-display text-xl">Golf & Sea — Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => fetchData(sessionStorage.getItem("admin_code"))}
            className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#a1a3a1] hover:text-[#d4af37] transition-colors duration-300"
            data-testid="admin-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
          <button onClick={logout} className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#a1a3a1] hover:text-[#d4af37] transition-colors duration-300" data-testid="admin-logout-btn">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10 max-w-6xl mx-auto">
        <div className="flex gap-px border border-white/15 w-fit mb-10">
          <button onClick={() => setTab("leads")} data-testid="admin-tab-leads"
            className={`flex items-center gap-2 px-6 py-3 text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${tab === "leads" ? "bg-[#d4af37] text-[#0a0c0b] font-medium" : "text-[#a1a3a1] hover:text-[#f2f0e9]"}`}>
            <Users size={14} /> Lead ({leads.length})
          </button>
          <button onClick={() => setTab("logs")} data-testid="admin-tab-logs"
            className={`flex items-center gap-2 px-6 py-3 text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${tab === "logs" ? "bg-[#d4af37] text-[#0a0c0b] font-medium" : "text-[#a1a3a1] hover:text-[#f2f0e9]"}`}>
            <KeyRound size={14} /> Accessi ({logs.length})
          </button>
        </div>

        {tab === "leads" ? (
          <div className="border border-white/10 overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  {["Tipo", "Nome", "Email", "Telefono", "Messaggio", "Lingua", "Data"].map((h) => (
                    <th key={h} className="p-4 text-xs tracking-[0.15em] uppercase text-[#8c877d] font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-200" data-testid={`lead-row-${l.id}`}>
                    <td className="p-4"><span className={`px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase border ${badge(l.lead_type)}`}>{l.lead_type === "investor" ? "Investitore" : "Acquirente"}</span></td>
                    <td className="p-4 text-[#f2f0e9]">{l.name || "—"}</td>
                    <td className="p-4 text-[#a1a3a1]">{l.email}</td>
                    <td className="p-4 text-[#a1a3a1]">{l.phone || "—"}</td>
                    <td className="p-4 text-[#a1a3a1] max-w-[240px] truncate">{l.message || "—"}</td>
                    <td className="p-4 uppercase text-[#8c877d]">{l.language}</td>
                    <td className="p-4 text-[#8c877d] whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#8c877d]">Nessun lead.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto" data-testid="admin-logs-table">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  {["Email", "Esito", "Lingua", "Data"].map((h) => (
                    <th key={h} className="p-4 text-xs tracking-[0.15em] uppercase text-[#8c877d] font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((a) => (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-200">
                    <td className="p-4 text-[#f2f0e9]">{a.email}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase border ${a.granted ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-red-500/10 text-red-400 border-red-500/30"}`}>
                        {a.granted ? "Concesso" : "Negato"}
                      </span>
                    </td>
                    <td className="p-4 uppercase text-[#8c877d]">{a.language}</td>
                    <td className="p-4 text-[#8c877d] whitespace-nowrap">{fmtDate(a.timestamp)}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#8c877d]">Nessun accesso registrato.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
